import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { rooms as mockRooms } from '@/mocks/rooms';
import { mapDbRoom, formatPrice, type Room } from '@/lib/rooms';

interface RoomsSectionProps {
  businessId: string;
  isReal: boolean;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function RoomsSection({ businessId, isReal }: RoomsSectionProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [checkIn, setCheckIn] = useState(todayISO());
  const [checkOut, setCheckOut] = useState(addDaysISO(1));
  const [guests, setGuests] = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState<{ nights: number; total: number } | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      if (isReal) {
        const { data } = await supabase
          .from('hotel_rooms')
          .select('*')
          .eq('business_id', businessId)
          .eq('status', 'active')
          .order('price_per_night', { ascending: true });
        if (mounted) {
          setRooms(((data as unknown[]) || []).map((r) => mapDbRoom(r as never)));
          setLoading(false);
        }
      } else {
        if (mounted) {
          setRooms(mockRooms.filter((r) => r.businessId === businessId));
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [businessId, isReal]);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn).getTime();
    const end = new Date(checkOut).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
    return Math.round((end - start) / (1000 * 60 * 60 * 24));
  }, [checkIn, checkOut]);

  const total = selectedRoom && nights > 0 ? nights * selectedRoom.pricePerNight : 0;

  const openBooking = (room: Room) => {
    setSelectedRoom(room);
    setGuests(Math.min(1, room.capacity));
    setCustomerName(user?.user_metadata?.full_name || '');
    setCustomerPhone('');
    setStatus('idle');
    setErrorMsg('');
    setResult(null);
  };

  const closeBooking = () => {
    setSelectedRoom(null);
    setStatus('idle');
    setErrorMsg('');
    setResult(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRoom) return;

    if (nights <= 0) {
      setStatus('error');
      setErrorMsg('La date de départ doit être postérieure à la date d\'arrivée.');
      return;
    }
    if (guests < 1 || guests > selectedRoom.capacity) {
      setStatus('error');
      setErrorMsg(`Le nombre de voyageurs doit être entre 1 et ${selectedRoom.capacity}.`);
      return;
    }

    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!isReal) {
      setStatus('success');
      setResult({ nights, total });
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    const { data, error } = await supabase.rpc('book_room', {
      p_room_id: selectedRoom.id,
      p_check_in: checkIn,
      p_check_out: checkOut,
      p_guests: guests,
      p_customer_name: customerName.trim() || null,
      p_customer_phone: customerPhone.trim() || null,
    });

    if (error) {
      setStatus('error');
      const msg = error.message || '';
      if (msg.toLowerCase().includes('insufficient')) {
        setErrorMsg('Solde insuffisant. Rechargez votre portefeuille avant de réserver.');
      } else if (msg.toLowerCase().includes('capacity')) {
        setErrorMsg(`Cette chambre ne peut accueillir que ${selectedRoom.capacity} personne(s).`);
      } else if (msg.toLowerCase().includes('available')) {
        setErrorMsg('Cette chambre n\'est plus disponible.');
      } else {
        setErrorMsg(msg || 'Une erreur est survenue lors de la réservation.');
      }
      return;
    }

    setStatus('success');
    setResult({ nights, total });
    void data;
  };

  if (loading) {
    return (
      <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 mb-6">
        <div className="flex items-center justify-center py-8">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
        </div>
      </div>
    );
  }

  if (rooms.length === 0) {
    return null;
  }

  return (
    <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 mb-6">
      <div className="flex items-center justify-between gap-4 mb-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <i className="ri-hotel-bed-line text-primary-400" />
          Chambres &amp; réservation
        </h2>
        <span className="text-xs text-foreground-400 whitespace-nowrap">{rooms.length} chambre{rooms.length > 1 ? 's' : ''}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {rooms.map((room) => (
          <div
            key={room.id}
            className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
          >
            <div className="relative h-40 w-full overflow-hidden">
              <img
                src={room.images[0]}
                alt={room.name}
                title={`${room.name} - chambre d'hôtel`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {!room.available && (
                <div className="absolute inset-0 bg-foreground-950/50 flex items-center justify-center">
                  <span className="px-3 py-1 rounded-full bg-foreground-950/70 text-foreground-50 text-xs font-semibold">
                    Indisponible
                  </span>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="font-heading font-semibold text-sm text-foreground-50">{room.name}</h3>
                <span className="font-heading font-bold text-primary-400 whitespace-nowrap">
                  {formatPrice(room.pricePerNight)}
                </span>
              </div>
              <p className="text-xs text-foreground-400 mb-2">
                {room.capacity} pers. · {room.bedType}
                <span className="text-foreground-500"> / nuit</span>
              </p>
              <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">{room.description}</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {room.amenities.slice(0, 4).map((a) => (
                  <span key={a} className="px-2 py-0.5 rounded-full bg-background-100 border border-background-200/70 text-[11px] text-foreground-400">
                    {a}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => openBooking(room)}
                disabled={!room.available}
                className="w-full px-4 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
              >
                Réserver
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Booking modal */}
      {selectedRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/60" onClick={closeBooking} />
          <div className="relative w-full max-w-md bg-background-50 rounded-xl border border-background-200/70 shadow-2xl max-h-[90vh] overflow-y-auto">
            {status === 'success' && result ? (
              <div className="p-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-500/15 mb-4">
                  <i className="ri-check-line text-3xl text-accent-400" />
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-50 mb-2">Réservation confirmée !</h3>
                <p className="text-sm text-foreground-400 mb-4">
                  {isReal
                    ? 'Votre paiement a été débité de votre portefeuille. L\'hôtel vous attend.'
                    : 'Démo : il s\'agit d\'un aperçu de la réservation.'}
                </p>
                <div className="p-4 rounded-lg bg-background-100 border border-background-200/70 text-left space-y-2 mb-5">
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-400">Chambre</span>
                    <span className="text-foreground-50 font-medium">{selectedRoom.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-400">Nuits</span>
                    <span className="text-foreground-50 font-medium">{result.nights}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-400">Total payé</span>
                    <span className="text-primary-400 font-bold">{formatPrice(result.total)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeBooking}
                  className="w-full px-4 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-foreground-50">Réserver : {selectedRoom.name}</h3>
                    <p className="text-sm text-foreground-400">{formatPrice(selectedRoom.pricePerNight)} / nuit</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeBooking}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-foreground-50 transition-colors"
                    aria-label="Fermer"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-foreground-400 mb-1.5">Arrivée</label>
                      <input
                        type="date"
                        min={todayISO()}
                        value={checkIn}
                        onChange={(e) => setCheckIn(e.target.value)}
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-50 outline-none focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground-400 mb-1.5">Départ</label>
                      <input
                        type="date"
                        min={checkIn || todayISO()}
                        value={checkOut}
                        onChange={(e) => setCheckOut(e.target.value)}
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-50 outline-none focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-foreground-400 mb-1.5">
                      Voyageurs (max {selectedRoom.capacity})
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={selectedRoom.capacity}
                      value={guests}
                      onChange={(e) => setGuests(Number(e.target.value))}
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-50 outline-none focus:border-primary-400"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-foreground-400 mb-1.5">Votre nom</label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Nom complet"
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground-400 mb-1.5">Téléphone</label>
                      <input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="+228 ..."
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-between">
                    <span className="text-sm text-foreground-400">
                      {nights > 0 ? `${nights} nuit${nights > 1 ? 's' : ''} · ${formatPrice(selectedRoom.pricePerNight)}` : 'Sélectionnez vos dates'}
                    </span>
                    <span className="font-heading font-bold text-primary-400">
                      {total > 0 ? formatPrice(total) : '—'}
                    </span>
                  </div>

                  {status === 'error' && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">{errorMsg}</div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting' || total <= 0}
                    className="w-full px-4 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                  >
                    {status === 'submitting'
                      ? 'Paiement...'
                      : user
                        ? `Confirmer et payer ${total > 0 ? formatPrice(total) : ''}`
                        : 'Se connecter pour réserver'}
                  </button>

                  <p className="text-xs text-foreground-400 flex items-center gap-1.5">
                    <i className="ri-shield-check-line text-primary-400" />
                    Le paiement est débité de votre portefeuille WorldApeiron Pay.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomsSection;